import { useMemo, useState } from 'react';
import { X } from 'lucide-react';
import {
  Combobox,
  ComboboxContent,
  ComboboxEmpty,
  ComboboxIcon,
  ComboboxItem,
  ComboboxList,
  ComboboxTrigger,
  ComboboxValue,
} from '@/components/ui/combobox';
import { LOADING_PLACEHOLDER } from '@/constants/app.constants';
import { cn } from '@/lib/utils';
import {
  COMBOBOX_LIST_CLASS,
  COMBOBOX_PANEL_CLASS,
  DROPDOWN_ITEM_CLASS,
} from '@/styles/floatingPanel';
import { ActiveStatusBadge } from '../ActiveStatusBadge';
import { ComboboxSearchField } from '../ComboboxSearchField';
import { isSameComboboxOption } from '@/utils/comboboxOption';
import { formatCompactNumber, formatNumber } from '@/utils/number';
import { ACCENT_MARK_CLASS } from '@/styles/accentMark';
import {
  COMPACT_FILTER_CONTROL_CLASS,
  FILTER_CONTROL_CLASS,
  HIDDEN_UNTIL_FILTER_FIELD_ACTIVE_CLASS,
} from './advancedFilter.constants';
import type { FilterField, FilterOption } from './advancedFilter.types';

const CLEAR_BUTTON_CLASS =
  'absolute top-1/2 right-3 flex size-4 -translate-y-1/2 cursor-pointer items-center justify-center rounded-sm text-muted-foreground transition-colors hover:bg-muted hover:text-foreground focus:outline-none focus-visible:ring-1 focus-visible:ring-ring';

const LOCAL_SEARCH_PLACEHOLDER = 'Search...';
const SERVER_SEARCH_PLACEHOLDER = 'Type to search...';
const NO_RESULTS_TEXT = 'No results found.';

interface OptionCountProps {
  count: number;
}

function OptionCount({ count }: OptionCountProps) {
  return (
    <span
      title={formatNumber(count)}
      className={cn(
        'flex size-4.5 shrink-0 items-center justify-center rounded-full text-4xs leading-none font-semibold tabular-nums',
        ACCENT_MARK_CLASS,
      )}
    >
      {formatCompactNumber(count)}
    </span>
  );
}

function isFilterOption(item: unknown): item is FilterOption {
  return typeof item === 'object' && item !== null && 'label' in item && 'value' in item;
}

function matchesFilterOption(item: unknown, query: string): boolean {
  const needle = query.trim().toLowerCase();
  if (needle === '' || !isFilterOption(item)) return true;
  return [item.label, item.searchText ?? ''].some((text) => text.toLowerCase().includes(needle));
}

interface FilterSelectFieldProps {
  field: FilterField;
  value: string;
  onChange: (value: string) => void;
}

export function FilterSelectField({ field, value, onChange }: FilterSelectFieldProps) {
  const allLabel = field.placeholder ?? 'All';
  const isClearable = field.isClearable ?? true;
  const showsClearButton = isClearable && value !== '';
  const searchesOnServer = field.onSearchChange !== undefined;
  const showsAllItem = isClearable && !searchesOnServer && !field.isRequired;
  const selectedValue = value === '' && !showsAllItem ? null : value;
  const [pickedOption, setPickedOption] = useState<FilterOption | undefined>(undefined);

  const items = useMemo<FilterOption[]>(() => {
    const options = field.options ?? [];
    const visibleOptions =
      searchesOnServer && pickedOption?.value === value
        ? [pickedOption, ...options.filter((option) => option.value !== value)]
        : options;
    return showsAllItem ? [{ label: allLabel, value: '' }, ...visibleOptions] : visibleOptions;
  }, [field.options, searchesOnServer, pickedOption, value, showsAllItem, allLabel]);

  function handleValueChange(nextValue: string | null) {
    const changedValue = nextValue ?? '';
    setPickedOption(items.find((item) => item.value === changedValue));
    onChange(changedValue);
  }

  return (
    <Combobox<string>
      items={items}
      value={selectedValue}
      onValueChange={handleValueChange}
      isItemEqualToValue={isSameComboboxOption}
      filter={field.searchable && !searchesOnServer ? matchesFilterOption : null}
      disabled={field.disabled}
      {...(field.onSearchChange ? { onInputValueChange: field.onSearchChange } : {})}
    >
      <div className="relative">
        <ComboboxTrigger
          className={cn(FILTER_CONTROL_CLASS, field.isCompact && COMPACT_FILTER_CONTROL_CLASS)}
        >
          <ComboboxValue
            placeholder={allLabel}
            className={cn(!value && HIDDEN_UNTIL_FILTER_FIELD_ACTIVE_CLASS)}
          />
          <ComboboxIcon className={showsClearButton ? 'invisible' : undefined} />
        </ComboboxTrigger>
        {showsClearButton ? (
          <button
            type="button"
            tabIndex={-1}
            aria-label={`Clear ${field.label}`}
            onClick={() => handleValueChange('')}
            className={CLEAR_BUTTON_CLASS}
          >
            <X className="size-3" />
          </button>
        ) : null}
      </div>
      <ComboboxContent className={COMBOBOX_PANEL_CLASS}>
        {field.searchable || searchesOnServer ? (
          <ComboboxSearchField
            placeholder={searchesOnServer ? SERVER_SEARCH_PLACEHOLDER : LOCAL_SEARCH_PLACEHOLDER}
          />
        ) : null}
        <ComboboxList className={COMBOBOX_LIST_CLASS}>
          {(item: FilterOption) => (
            <ComboboxItem key={item.value} value={item.value} className={DROPDOWN_ITEM_CLASS}>
              {item.description ? (
                <span className="flex min-w-0 flex-col gap-0.5">
                  <span className="truncate font-medium">{item.label}</span>
                  <span className="truncate text-xs text-muted-foreground">{item.description}</span>
                </span>
              ) : (
                <span className="min-w-0 truncate">{item.label}</span>
              )}
              {item.count !== undefined && item.count > 0 && <OptionCount count={item.count} />}
              {item.isActive === undefined ? null : (
                <span className="ml-auto shrink-0 pl-3">
                  <ActiveStatusBadge isActive={item.isActive} />
                </span>
              )}
            </ComboboxItem>
          )}
        </ComboboxList>
        <ComboboxEmpty>{field.isSearching ? LOADING_PLACEHOLDER : NO_RESULTS_TEXT}</ComboboxEmpty>
      </ComboboxContent>
    </Combobox>
  );
}
